(ns minorodata.dependency-graph.events
  "Background/data layer for the standalone dependency-graph page.

   Mirrors the pipeline editor's fetch → parse → layout → store flow
   (`minorodata.sandbox.events`) but for the FULL graph (no anchor). Parsing,
   sizing and ELK layout — including the group/source clustering — are shared with
   the editor in `minorodata.pipeline-dag.layout`; this ns owns the global page's
   filtering (group/tag/visibility) and its tab + persistence plumbing."
  (:require
   [clojure.string :as str]
   [datascript.core :as datascript]
   [minorodata.app.db :refer [conn]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.pipeline-dag.layout :as layout :refer [default-layout
                                                      parse-raw-edge
                                                      parse-raw-node]]
   [minorodata.pipeline-dag.timing :as timing]
   [minorodata.sandbox.model :refer [->pipeline-dag]]
   [minorodata.unleash.core :refer [unleash]]
   [re-frame.core :refer [reg-event-db reg-event-fx reg-fx]]))


;; The default, unclosable "Pipelines" tab renders the FULL graph; its
;; `pipeline-dag` entity / `:raw` slot is keyed by this id. Anchored tabs reuse
;; the same id namespace, keyed by their anchor node id.
(def dag-id "dependency-graph:full")

(defn anchor-tab-id
  "Stable per-tab id (and `pipeline-dag` entity id) for an anchored subpipeline."
  [anchor-id]
  (str "dependency-graph:anchor:" anchor-id))

(def pipelines-tab
  {:id dag-id :label "Pipelines" :closable? false})

;; The whole dependency-graph view state (layout, filters, extract visibility,
;; open tabs, …) is persisted to localStorage so the page reopens exactly as the
;; user left it — see `persist`/`restore-state`. Everything except the fetched
;; graph data (`transient-state-keys`) is saved.
;; Keyed by client and user, as tabs carry entity names; nil until both are known.
(defn- state-ls-key [db]
  (let [{:keys [client-id user-id]} (:app db)]
    (when (and client-id user-id)
      (str "dependency-graph-state:" client-id ":" user-id))))

;; The old unkeyed state: removed unread, as it may be another client's.
(def ^:private legacy-state-ls-key "dependency-graph-state")

;; Server data / per-session view state living under `:dependency-graph` that is
;; refetched or recomputed on init, so it's stripped before persisting. Notably
;; `:raw` holds every tab's nodes/edges — large and always refetched.
(def ^:private transient-state-keys
  [:raw :laid-out :layout-failed :layout-seq :layout-pending :ungroup-limit :pending-focus :logs :timeline :focus-node-id :focus-tick
   :hovered-master-id])


(defn- read-persisted-state
  "The parsed localStorage payload, or nil when nothing usable is stored. Anything that
   isn't a JSON object — a write truncated by a killed tab, a shape from an older build,
   a hand-edited value — is dropped AND cleared, so a corrupt value self-heals instead
   of being re-read on every reload. Failing closed matters here: `restore-state` runs
   during render (`:dependency-graph.subs/layout`) and inside `init`, which is
   registered with raw `reg-event-fx` and so has no try/catch boundary."
  [ls-key]
  (when-let [stored (some-> ls-key js/localStorage.getItem)]
    (let [parsed (try
                   (js->clj (js/JSON.parse stored) :keywordize-keys true)
                   (catch :default _ ::corrupt))]
      (if (map? parsed)
        parsed
        (do (js/localStorage.removeItem ls-key)
            nil)))))

(defn restore-state
  "The dependency-graph view state saved to localStorage, or nil when nothing/garbage
   is stored. Coerces the group/tag filters back to sets after the JSON round-trip,
   and ignores any stored layout preset (there is no longer a way to change it). Migrates the legacy single-select `:group-id`
   into the multi-select `:group-ids` set so older persisted state keeps working."
  [db]
  (js/localStorage.removeItem legacy-state-ls-key)
  (when-let [stored (read-persisted-state (state-ls-key db))]
    (cond-> (dissoc stored :layout)
      (:filters stored) (update :filters
                                (fn [{:keys [group-id group-ids tags]}]
                                  {:group-ids (set (or group-ids
                                                       (when group-id [group-id])))
                                   :tags      (set tags)})))))


;; =============================================================================
;; Events
;; =============================================================================

(defn- show-sources? [db]
  (get-in db [:dependency-graph :show-sources?] true))

(defn- show-tables? [db]
  (get-in db [:dependency-graph :show-tables?] false))

(defn- show-groups? [db]
  (get-in db [:dependency-graph :show-groups?] true))

(def ^:private failure-floor
  "Smallest grouped failure taken as a size problem, lowering the session's ungroup limit."
  500)

(defn- groups-for
  "Whether `n` nodes lay out grouped: not when the user turned grouping off, nor
   after a grouped layout failed."
  [db n flat?]
  (let [limit (get-in db [:dependency-graph :ungroup-limit])]
    (and (show-groups? db)
         (not flat?)
         (not (and limit (>= n limit))))))

(defn- edge-endpoints [edge]
  (if (sequential? edge) edge [(:source edge) (:target edge)]))

(defn- keep-nodes
  "Keep the nodes satisfying `keep?` and drop every edge left dangling by their
   removal. The shared shape behind `filter-sources`
   and `filter-entities`."
  [{:keys [nodes edges]} keep?]
  (let [kept (filterv keep? nodes)
        ids  (into #{} (map :node-id) kept)]
    {:nodes kept
     :edges (filterv (fn [edge]
                       (let [[s t] (edge-endpoints edge)]
                         (and (ids s) (ids t))))
                     edges)}))

(defn- contract-tables
  "Elide `table` nodes: reconnect each non-table producer directly to the
   non-table consumers it reaches *through* one or more tables, so
   extract→table→transform collapses to extract→transform."
  [{:keys [nodes edges]}]
  (let [table-id? (into #{} (comp (filter #(= "table" (:type %))) (map :node-id)) nodes)
        succ      (reduce (fn [m edge]
                            (let [[s t] (edge-endpoints edge)]
                              (update m s (fnil conj #{}) t)))
                          {} edges)
        ;; non-table nodes reachable from `id` through table-only intermediate paths
        reach     (fn reach [id seen]
                    (reduce (fn [acc t]
                              (cond
                                (seen t)      acc
                                (table-id? t) (into acc (reach t (conj seen t)))
                                :else         (conj acc t)))
                            #{} (succ id)))
        kept      (filterv #(not (table-id? (:node-id %))) nodes)
        kept-ids  (set (map :node-id kept))]
    {:nodes kept
     :edges (->> kept
                 (mapcat (fn [{:keys [node-id]}]
                           (for [tgt   (reach node-id #{node-id})
                                 :when (and (not= node-id tgt) (kept-ids tgt))]
                             [node-id tgt])))
                 distinct
                 vec)}))

(defn graph-extract-ids
  "Set of extract-ids (the metadata ids that match status `:extract-id`) of every
   extract in a tab's stored `{:nodes :edges}` — all of them are drawn, including
   those feeding no transform."
  [raw]
  (into #{}
        (comp (filter #(= "extract" (:type %)))
              (keep #(get-in % [:metadata :extract-id])))
        (:nodes raw)))

(defn- filter-sources
  "Drop extract (source) nodes and any edge touching them when sources are hidden."
  [{:keys [nodes edges]} keep-sources?]
  (if keep-sources?
    {:nodes nodes :edges edges}
    (keep-nodes {:nodes nodes :edges edges} #(not= "extract" (:type %)))))

(defn- prune-edgeless-tables
  "When tables are shown, drop any table node with no edge attached so the canvas
   only carries tables that actually connect something. A no-op when tables are
   contracted (there are no table nodes) or when a table only touched a now-hidden
   source — run this last, after `filter-sources`, so hidden-source edges count as
   removed."
  [{:keys [nodes edges]}]
  (let [connected (reduce (fn [acc edge]
                            (let [[s t] (edge-endpoints edge)]
                              (-> acc (conj s) (conj t))))
                          #{} edges)
        kept      (filterv (fn [{:keys [node-id type]}]
                             (or (not= "table" type)
                                 (contains? connected node-id)))
                           nodes)]
    {:nodes kept :edges edges}))


;; -----------------------------------------------------------------------------
;; Group / tag filter — DROPS non-matching nodes and re-runs ELK (unlike the old
;; in-place dimming), so applying a filter re-packs the graph around the survivors.
;; -----------------------------------------------------------------------------

(defn tags-by-id
  "`transform-id/master-id -> tags` from a seq of pulled `pipeline-transform`
   maps (tags a plain string vector, cardinality-one). Keyed by BOTH ids so a
   graph node resolves regardless of which the backend stamped on it. Shared by
   the event-side `transform-tags-by-id` and `:dependency-graph.subs/transform-tags`."
  [transforms]
  (reduce (fn [m {:pipeline-transform/keys [id master-id tags]}]
            (let [tags (vec (remove str/blank? tags))]
              (cond-> m
                (some? id)        (assoc id tags)
                (some? master-id) (assoc master-id tags))))
          {}
          transforms))

(defn transform-tags-by-id
  "`tags-by-id` over every `pipeline-transform` entity loaded by `fetch-transforms`,
   read straight from DataScript for use outside a reactive context."
  []
  (tags-by-id
   ;; pull (not a 3-var :find) so entities missing the optional master-id
   ;; still come back — a `get-else` clause would throw on the nil default.
   (datascript/q '[:find [(pull ?e [:pipeline-transform/id
                                    :pipeline-transform/master-id
                                    :pipeline-transform/tags]) ...]
                   :where [?e :pipeline-transform/tags _]]
                 @conn)))

(defn- raw-node-group-id
  "The group/source/model-group id a RAW node belongs to — what the group filter
   matches against. Extracts group by source-id, models resolve their instance to
   a registry model (as `assign-model-groups` does). nil for tables / ungrouped."
  [{:keys [type metadata]} model->registry]
  (case type
    "transform" (:group-id metadata)
    "extract"   (:source-id metadata)
    "model"     (get model->registry (:model-id metadata))
    nil))

(defn- raw-node-matches-filter?
  "Does a RAW entity node survive the active group/tag filter? Group and tags are
   ANDed; a node matches the group when its resolved group id is among the selected
   set, and the tags when it carries ANY selected tag. Mirrors the old
   `node-matches-filter?` but works off raw metadata + a tags lookup."
  [node filters model->registry tags-by-id]
  ;; `group-ids`/`tags` are already sets (db invariant — see `toggle-in-set` /
  ;; `restore-state`), so they're used directly as membership predicates.
  (let [{:keys [group-ids tags]} filters
        group-ok? (or (empty? group-ids)
                      (contains? group-ids (raw-node-group-id node model->registry)))
        node-tags (get tags-by-id (get-in node [:metadata :transform-id]) [])
        tag-ok?   (or (empty? tags)
                      (boolean (some tags node-tags)))]
    (and group-ok? tag-ok?)))

(defn- filter-entities
  "Drop entity nodes (transform/extract/model) that don't survive the active
   group/tag filter, plus any edge left dangling. Tables pass through. A no-op
   when no filter is active."
  [{:as graph} filters model->registry tags-by-id]
  (if-not (or (seq (:group-ids filters)) (seq (:tags filters)))
    graph
    (let [entity? #{"transform" "extract" "model"}]
      (keep-nodes graph
                  (fn [{:keys [type] :as node}]
                    (or (not (entity? type))
                        (raw-node-matches-filter? node filters model->registry tags-by-id)))))))

(reg-event-fx
 :dependency-graph.events/init
 (fn [{:keys [db]} [_ {:keys [focus-id timeline-id]}]]
   (let [defaults  {:show-sources?       true
                    :show-tables?        false
                    :show-groups?        true
                    :compact?            false
                    :failures-collapsed? false
                    :auto-refresh-ms     nil
                    :filters             {:group-ids #{} :tags #{}}
                    :layout              default-layout
                    :tabs                [pipelines-tab]
                    :active-tab-id       dag-id}
         restored  (merge defaults (restore-state db))
         ;; the default "Pipelines" tab is always present and implicit; only the
         ;; persisted *anchored* subpipeline tabs need re-opening alongside it.
         anchored  (filterv :anchor-id (:tabs restored))
         tabs      (into [pipelines-tab] anchored)
         tab-ids   (set (map :id tabs))
         ;; keep the persisted active tab only if it still exists, else fall back
         ;; to the Pipelines tab (e.g. its subpipeline was deleted since).
         active-id (if (contains? tab-ids (:active-tab-id restored))
                     (:active-tab-id restored)
                     dag-id)
         ;; `/dag?focus=<master-id>` — held until the graph it refers to arrives,
         ;; which is the first point we can tell whether the entity is in it.
         state     (cond-> (assoc restored :tabs tabs :active-tab-id active-id)
                     focus-id (assoc :pending-focus focus-id)
                     ;; `?tl=`: the timeline pane that was open
                     (and timeline-id (.isEnabled unleash "fe.execution-timeline"))
                     (assoc :timeline {:master-id timeline-id}))]
     {:db         (update db :dependency-graph merge state)
      ;; re-arm auto-refresh if the user had it on before reloading
      :dependency-graph/auto-refresh-timer (:auto-refresh-ms state)
      :dispatch-n (into
                   [[:pipeline-dag/add (->pipeline-dag {:id dag-id :filters {}})]
                    ;; FIRST, deliberately: the only fetch the canvas cannot draw
                    ;; without, and the slowest. Everything below merely enriches nodes
                    ;; already on screen, so the graph should not queue behind it.
                    [:dependency-graph.api/fetch-graph {:tab-id dag-id}]
                    ;; populate group/source names used for node subtitles + box labels
                    [:pipeline-editor.api/fetch-transform-groups]
                    [:connections.api/fetch-sources]
                    ;; pull every transform's tags for the group/tag filter toolbar —
                    ;; the live graph nodes don't carry tags (see fetch-transforms).
                    [:dependency-graph.api/fetch-transforms]
                    ;; Model nodes are grouped by their instance's registry model:
                    ;; the instances supply the `model-id -> registry-model` group key
                    ;; (see `model-id->registry-model`), the registry supplies each
                    ;; group's display name. Both gated on the analytics-models feature.
                    (when (.isEnabled unleash "fe.analytics-models")
                      [:analytics-models.api/fetch-models])
                    (when (.isEnabled unleash "fe.analytics-models")
                      [:analytics-models.api/fetch-model-registry])
                    ;; ...and their last-run status. Nothing else on this page pulls
                    ;; it, so without this every model node read "Never executed".
                    (when (.isEnabled unleash "fe.analytics-models")
                      [:analytics-models.api/fetch-statuses])
                    ;; populate per-transform / per-extract last-run status shown on nodes
                    ;; (no :status filter → all statuses, not just failures).
                    [:connections.api/fetch-transform-statuses]
                    [:connections.api/fetch-extract-statuses-for-graph]]
                   ;; re-hydrate each persisted subpipeline tab: re-add its dag entity
                   ;; and refetch its graph (nodes/edges aren't persisted). `:restore?`
                   ;; makes a tab whose subpipeline no longer exists drop silently when
                   ;; its fetch fails, instead of surfacing an error snack.
                   (mapcat (fn [{:keys [id anchor-id anchor-type]}]
                             [[:pipeline-dag/add (->pipeline-dag {:id id :filters {}})]
                              [:dependency-graph.api/fetch-graph
                               {:tab-id id :anchor-id anchor-id :anchor-type anchor-type :restore? true}]])
                           anchored))})))

(reg-event-fx
 :dependency-graph.events/receive-graph
 (fn [{:keys [db]} [_ {:keys [tab-id nodes edges]}]]
   (timing/end! :fetch)
   {:db       (assoc-in db [:dependency-graph :raw tab-id] {:nodes (vec nodes) :edges (vec edges)})
    :dispatch [:dependency-graph.events/layout-graph tab-id]}))

(reg-event-fx
 :dependency-graph.events/layout-graph
 (fn [{:keys [db]} [_ tab-id {:keys [flat?]}]]
   (if-let [raw (get-in db [:dependency-graph :raw tab-id])]
     (let [preset          (get-in db [:dependency-graph :layout] default-layout)
           filters         (get-in db [:dependency-graph :filters])
           ;; resolved once and shared by the group/tag filter and model boxing
           model->registry (layout/model-id->registry-model)
           ;; only needed when a tag filter is active — `filter-entities` is a
           ;; no-op otherwise, so skip the whole-conn query on the common path.
           tags-by-id      (when (seq (:tags filters)) (transform-tags-by-id))
           ;; Filtering runs on the RAW (backend-shaped) graph — `keep-nodes` and
           ;; `filter-entities` key off `:node-id` / `:metadata` — so parse only once
           ;; the survivors are known.
           {:keys [nodes edges]} (timing/timed
                                  :prune
                                  #(-> raw
                                       (cond-> (not (show-tables? db)) contract-tables)
                                       (filter-entities filters model->registry tags-by-id)
                                       (filter-sources (show-sources? db))
                                       prune-edgeless-tables))
           n (count nodes)
           groups? (groups-for db n flat?)
           ;; only the latest run per tab lands, and it clears the relayout banner
           seq-id (inc (get-in db [:dependency-graph :layout-seq] 0))]
       (-> (layout/do-layout {:nodes (mapv parse-raw-node nodes)
                              :edges (mapv parse-raw-edge edges)}
                             {:preset          preset
                              :groups?         groups?
                              :density         (if (get-in db [:dependency-graph :compact?])
                                                 :dense
                                                 :comfortable)
                              ;; Tables lay out as the compact icon-only pill, the
                              ;; same as the editor — see
                              ;; `components.dependency-graph.dag/table-node'` for
                              ;; why, and note the two MUST agree or ELK sizes a
                              ;; node the DOM then renders at another size.
                              :table-shape     :pill
                              :model->registry model->registry
                              :user-id         (get-in db [:app :user-id])})
           (.then  (fn [result] (dispatch [:dependency-graph.events/layout-success tab-id result seq-id])))
           (.catch (fn [error]  (dispatch [:dependency-graph.events/layout-failure tab-id error
                                           {:grouped? groups? :nodes n :seq-id seq-id}]))))
       {:db (-> db
                (assoc-in [:dependency-graph :layout-seq] seq-id)
                (assoc-in [:dependency-graph :layout-pending tab-id] seq-id))})
     {})))

;; The layout/sources controls are global, so re-lay-out every open tab when they change.
(reg-event-fx
 :dependency-graph.events/layout-all
 (fn [{:keys [db]} _]
   {:dispatch-n (mapv (fn [tab-id] [:dependency-graph.events/layout-graph tab-id])
                      (keys (get-in db [:dependency-graph :raw])))}))

(reg-event-fx
 :dependency-graph.events/layout-success
 (fn [{:keys [db]} [_ tab-id {:keys [nodes edges]} seq-id]]
   ;; `:laid-out` is what `tab-ready?` waits on: the raw graph landing is not enough
   ;; to draw with, ELK runs after it.
   ;;
   ;; A `?focus=` link is also honoured here rather than on arrival of the graph:
   ;; centring is a viewport move, so the node has to be laid out and rendered first.
   ;; Cleared as it is consumed, so a later relayout doesn't yank the viewport back.
   (when (= seq-id (get-in db [:dependency-graph :layout-pending tab-id]))
     (let [focus (when (= tab-id dag-id) (get-in db [:dependency-graph :pending-focus]))]
       {:db         (cond-> (-> (assoc-in db [:dependency-graph :laid-out tab-id] true)
                                (update-in [:dependency-graph :layout-failed] dissoc tab-id)
                                (update-in [:dependency-graph :layout-pending] dissoc tab-id))
                      focus (update :dependency-graph dissoc :pending-focus))
        :dispatch-n (cond-> [[:pipeline-dag/update [:pipeline-dag/id tab-id]
                              {:pipeline-dag/nodes nodes
                               :pipeline-dag/edges edges}]]
                      focus (conj [:dependency-graph.events/focus-linked-node focus]))}))))

(reg-event-fx
 :dependency-graph.events/layout-failure
 (fn [{:keys [db]} [_ tab-id error {:keys [grouped? nodes seq-id]}]]
   (js/console.error "[dependency-graph layout failed]:" error)
   (cond
     (not= seq-id (get-in db [:dependency-graph :layout-pending tab-id]))
     {}

     grouped?
     ;; retry this tab flat; a large failure also keeps graphs this big flat for the session
     {:db       (cond-> db
                  (>= nodes failure-floor)
                  (update-in [:dependency-graph :ungroup-limit] #(min (or % ##Inf) nodes)))
      :dispatch [:dependency-graph.events/layout-graph tab-id {:flat? true}]}

     :else
     ;; Mark it laid out anyway — nothing further is coming, and `tab-ready?` would
     ;; otherwise hold the tab behind a spinner that never resolves. Drop any
     ;; `?focus=` with it: the layout it was waiting for isn't coming, and left set it
     ;; would fire on whatever relayout happens next (a filter change, minutes later).
     {:db (cond-> (-> (assoc-in db [:dependency-graph :laid-out tab-id] true)
                      (assoc-in [:dependency-graph :layout-failed tab-id] true)
                      (update-in [:dependency-graph :layout-pending] dissoc tab-id))
            (= tab-id dag-id) (update :dependency-graph dissoc :pending-focus))})))

;; Save the whole view state (layout, filters, extract visibility, open tabs, …)
;; to localStorage so the page reopens as the user left it. Dispatched as an
;; effect by every event that mutates persisted state. See `restore-state`.
;; A preview tab is not persisted (see `open-anchored-tab`).
(reg-event-fx
 :dependency-graph.events/persist
 (fn [{:keys [db]} _]
   (if-let [ls-key (state-ls-key db)]
     {:set-local-storage [ls-key (-> (apply dissoc (:dependency-graph db)
                                          transient-state-keys)
                                   (update :tabs #(filterv (complement :preview?) %)))]}
     {})))

;; Every view control that reshapes the graph (sources/tables/groups visibility,
;; layout preset, group/tag filters) does the same two things: persist the choice
;; and re-lay-out every open tab so the survivors re-pack.
(def ^:private persist-and-relayout-fx
  [[:dispatch [:dependency-graph.events/persist]]
   [:dispatch [:dependency-graph.events/layout-all]]])

(def ^:private view-setting-names
  {:show-sources? "extracts"
   :show-tables?  "tables"
   :show-groups?  "groups"})

(defn- toggle-view-setting [db flag]
  (let [db' (update-in db [:dependency-graph flag] not)]
    {:db db'
     :fx (conj persist-and-relayout-fx
               [:posthog/capture {:event (:pipeline/view-setting-toggled posthog-events/events)
                                  :props {:setting (view-setting-names flag)
                                          :visible (get-in db' [:dependency-graph flag])}}])}))

(reg-event-fx
 :dependency-graph.events/toggle-sources
 (fn [{:keys [db]} _]
   (toggle-view-setting db :show-sources?)))

(reg-event-fx
 :dependency-graph.events/toggle-tables
 (fn [{:keys [db]} _]
   (toggle-view-setting db :show-tables?)))

;; Cluster nodes into group/source/model-group boxes (default) or lay them out
;; flat. Like the sources/tables toggles this re-runs ELK for every open tab.
(reg-event-fx
 :dependency-graph.events/toggle-groups
 (fn [{:keys [db]} _]
   (toggle-view-setting db :show-groups?)))

(reg-event-fx
 :dependency-graph.events/toggle-compact
 (fn [{:keys [db]} _]
   {:db (update-in db [:dependency-graph :compact?] not)
    :fx persist-and-relayout-fx}))

;; -----------------------------------------------------------------------------
;; Group / tag filters
;;
;; Like `show-sources?`, the group/tag filters DROP non-matching nodes and re-run
;; ELK, so every change relays out the graph around the survivors (see
;; `filter-entities` in `layout-graph`). A filter is "active" when at least one
;; group or tag is selected; an empty selection shows everything. Every mutation
;; persists the choice and relays out all open tabs.
;; -----------------------------------------------------------------------------

;; Toggle one value in a multi-select filter set (`:group-ids` or `:tags`), adding
;; it when absent and removing it when present. An empty set means "no filter" for
;; that dimension (show all).
(defn- toggle-in-set [db filter-key value]
  (update-in db [:dependency-graph :filters filter-key]
             (fn [values]
               (let [values (set values)]
                 (if (contains? values value) (disj values value) (conj values value))))))

;; Multi-select group/source/model-group — toggling a group on/off; a node matches
;; if its resolved group id is among the selected set. Empty set shows all groups.
(reg-event-fx
 :dependency-graph.events/toggle-filter-group
 (fn [{:keys [db]} [_ group-id]]
   (let [db' (toggle-in-set db :group-ids group-id)]
     {:db db'
      :fx (conj persist-and-relayout-fx
                [:posthog/capture {:event (:pipeline/group-filter-toggled posthog-events/events)
                                   :props {:group-id group-id
                                           :selected (contains? (get-in db' [:dependency-graph :filters :group-ids])
                                                                group-id)}}])})))

;; Multi-select tags — toggling a tag on/off; a node matches if it carries ANY
;; selected tag (mirrors bleene's `job-has-any-of-tags?`).
(reg-event-fx
 :dependency-graph.events/toggle-filter-tag
 (fn [{:keys [db]} [_ tag]]
   {:db (toggle-in-set db :tags tag)
    :fx persist-and-relayout-fx}))

(reg-event-fx
 :dependency-graph.events/clear-filters
 (fn [{:keys [db]} _]
   {:db (assoc-in db [:dependency-graph :filters] {:group-ids #{} :tags #{}})
    :fx persist-and-relayout-fx}))

(reg-event-fx
 :dependency-graph.events/toggle-failures
 (fn [{:keys [db]} _]
   (let [collapsed? (not (get-in db [:dependency-graph :failures-collapsed?]))]
     {:db              (assoc-in db [:dependency-graph :failures-collapsed?] collapsed?)
      :dispatch        [:dependency-graph.events/persist]
      :posthog/capture {:event (:pipeline/summary-panel-toggled posthog-events/events)
                        :props {:visible (not collapsed?)}}})))


;; -----------------------------------------------------------------------------
;; Status refresh — re-fetch transform / extract / model last-run statuses (the
;; same fetches `init` fires), on demand or on an auto-refresh interval.
;; -----------------------------------------------------------------------------

;; Re-pull per-node statuses without re-fetching/relaying out the graph itself.
;; Models are gated as in `init` — no feature, no model nodes to refresh.
(reg-event-fx
 :dependency-graph.events/refresh-statuses
 (fn [_ _]
   {:dispatch-n (cond-> [[:connections.api/fetch-transform-statuses]
                         [:connections.api/fetch-extract-statuses-for-graph]]
                  (.isEnabled unleash "fe.analytics-models")
                  (conj [:analytics-models.api/fetch-statuses]))}))

;; A single JS interval owned by this fx. Passing an interval (ms) (re)starts it,
;; clearing any previous one first; nil just clears. Kept in a module-level atom
;; rather than the db since it's an opaque handle, not view state.
(defonce ^:private auto-refresh-timer-id (atom nil))

(reg-fx
 :dependency-graph/auto-refresh-timer
 (fn [interval-ms]
   (when-let [id @auto-refresh-timer-id]
     (js/clearInterval id)
     (reset! auto-refresh-timer-id nil))
   (when (and interval-ms (pos? interval-ms))
     (reset! auto-refresh-timer-id
             (js/setInterval #(dispatch [:dependency-graph.events/refresh-statuses])
                             interval-ms)))))

;; Set (or clear, with nil) the auto-refresh interval. Persisted so it survives
;; reloads (re-armed by `init`).
(reg-event-fx
 :dependency-graph.events/set-auto-refresh
 (fn [{:keys [db]} [_ interval-ms]]
   {:db (assoc-in db [:dependency-graph :auto-refresh-ms] interval-ms)
    :fx [[:dependency-graph/auto-refresh-timer interval-ms]
         [:dispatch [:dependency-graph.events/persist]]]}))



;; =============================================================================
;; Tabs — a default "Pipelines" tab plus closable anchored subpipeline tabs
;; =============================================================================

;; `preview?` opens an editor-style preview tab: at most one, replaced in place by
;; the next, not persisted until `pin-tab` (double-click) keeps it.
(reg-event-fx
 :dependency-graph.events/open-anchored-tab
 (fn [{:keys [db]} [_ {:keys [anchor-id anchor-type label preview?]}]]
   (let [tab-id    (anchor-tab-id anchor-id)
         tabs      (get-in db [:dependency-graph :tabs])
         existing? (some #(= (:id %) tab-id) tabs)
         replaced  (when preview? (:id (some #(when (:preview? %) %) tabs)))
         tab       (cond-> {:id          tab-id
                            :anchor-id   anchor-id
                            :anchor-type anchor-type
                            :label       (or label anchor-id)
                            :closable?   true}
                     preview? (assoc :preview? true))]
     (if existing?
       ;; already open — just focus it
       {:db       (assoc-in db [:dependency-graph :active-tab-id] tab-id)
        :dispatch [:dependency-graph.events/persist]}
       {:db         (-> db
                        (assoc-in [:dependency-graph :tabs]
                                  (if replaced
                                    (mapv #(if (= (:id %) replaced) tab %) tabs)
                                    (conj (or tabs []) tab)))
                        (cond-> replaced (update-in [:dependency-graph :raw] dissoc replaced))
                        (assoc-in [:dependency-graph :active-tab-id] tab-id))
        :dispatch-n (into (if replaced [[:pipeline-dag/delete [:pipeline-dag/id replaced]]] [])
                          [[:pipeline-dag/add (->pipeline-dag {:id tab-id :filters {}})]
                           [:dependency-graph.api/fetch-graph {:tab-id      tab-id
                                                               :anchor-id   anchor-id
                                                               :anchor-type anchor-type}]
                           [:dependency-graph.events/persist]])}))))

(reg-event-fx
 :dependency-graph.events/pin-tab
 (fn [{:keys [db]} [_ tab-id]]
   {:db       (update-in db [:dependency-graph :tabs]
                         (fn [tabs] (mapv #(if (= (:id %) tab-id) (dissoc % :preview?) %) tabs)))
    :dispatch [:dependency-graph.events/persist]}))

(reg-event-fx
 :dependency-graph.events/select-tab
 (fn [{:keys [db]} [_ tab-id]]
   {:db       (assoc-in db [:dependency-graph :active-tab-id] tab-id)
    :dispatch [:dependency-graph.events/persist]}))

(reg-event-fx
 :dependency-graph.events/close-tab
 (fn [{:keys [db]} [_ tab-id]]
   (let [tabs       (get-in db [:dependency-graph :tabs])
         active-id  (get-in db [:dependency-graph :active-tab-id])
         idx        (first (keep-indexed (fn [i t] (when (= (:id t) tab-id) i)) tabs))
         new-tabs   (filterv #(not= (:id %) tab-id) tabs)
         new-active (if (= active-id tab-id)
                      (:id (or (get new-tabs (max 0 (dec (or idx 0)))) (first new-tabs)))
                      active-id)]
     {:db       (-> db
                    (assoc-in [:dependency-graph :tabs] new-tabs)
                    (assoc-in [:dependency-graph :active-tab-id] new-active)
                    (update-in [:dependency-graph :raw] dissoc tab-id))
      :dispatch-n [[:pipeline-dag/delete [:pipeline-dag/id tab-id]]
                   [:dependency-graph.events/persist]]})))


;; =============================================================================
;; Execution timeline — a bottom pane (gantt + executions table) for one node.
;; State lives in `minorodata.dependency-graph.timeline.*`; here we only track
;; which node's timeline is open + the node to focus on.
;; =============================================================================

;; The timeline and logs share the single bottom pane (see
;; `minorodata.components.dependency-graph.page`), so opening one closes the other.
;; The open pane is kept in the URL as `?tl=<master-id>` (replaced, not pushed).
(def ^:private timeline-closed-query
  {:tl nil :tl-from nil :tl-to nil :tl-sub-from nil :tl-sub-to nil})

(reg-event-fx
 :dependency-graph.events/open-timeline
 (fn [{:keys [db]} [_ {:keys [master-id name source]}]]
   {:db              (-> db
                         (update :dependency-graph dissoc :logs)
                         (assoc-in [:dependency-graph :timeline] {:master-id master-id :name name}))
    :dispatch        [:app.events/replace-query {:tl master-id}]
    :posthog/capture {:event (:pipeline/execution-timeline-opened posthog-events/events)
                      :props {:source source}}}))

(reg-event-fx
 :dependency-graph.events/close-timeline
 (fn [{:keys [db]} _]
   ;; `hovered-master-id` is driven by the timeline's log rows, so it has no
   ;; source of a clearing mouse-leave once the pane is gone.
   {:db       (update db :dependency-graph dissoc :timeline :hovered-master-id)
    :dispatch [:app.events/replace-query timeline-closed-query]}))

;; Logs bottom pane — mirrors the timeline pane. `filter` is a map of namespaced
;; `:logs-view/*` keys (term/source) that the pane applies to the shared logs view.
(reg-event-fx
 :dependency-graph.events/open-logs
 (fn [{:keys [db]} [_ {:keys [name filter]}]]
   {:db       (-> db
                  (update :dependency-graph dissoc :timeline)
                  (assoc-in [:dependency-graph :logs] {:name name :filter filter}))
    :dispatch [:app.events/replace-query timeline-closed-query]}))

(reg-event-db
 :dependency-graph.events/close-logs
 (fn [db _]
   (update db :dependency-graph dissoc :logs)))

;; Clicking a row in the timeline (or "Focus" in the failures sidebar) centers the
;; matching node in the active canvas. The `:focus-tick` bumps on every dispatch so
;; re-focusing the *same* node still re-fires the centering effect (see
;; `ui.dag/viewport-controller`) — otherwise nothing happens when the id is unchanged.
(reg-event-db
 :dependency-graph.events/focus-node
 (fn [db [_ node-id]]
   (-> db
       (assoc-in [:dependency-graph :focus-node-id] node-id)
       (update-in [:dependency-graph :focus-tick] (fnil inc 0)))))

;; The focused node's lineage is pinned on the canvas; this unpins it.
(reg-event-db
 :dependency-graph.events/clear-focus
 (fn [db _]
   (update db :dependency-graph dissoc :focus-node-id)))

;; Master id of the execution-log row the pointer is on, so the matching graph
;; node can highlight. Read per-node (`node-hovered?`) rather than folded into the
;; nodes themselves: that way a hover re-renders the two nodes whose state changed
;; instead of rebuilding the whole node list.
(reg-event-db
 :dependency-graph.events/hover-node
 (fn [db [_ master-id]]
   (assoc-in db [:dependency-graph :hovered-master-id] master-id)))


(defn- node-by-entity
  "Find the raw \"Pipelines\"-tab node whose metadata matches a failing entity,
   returning the anchor info `open-anchored-tab` needs. `kind` is :transform-id or
   :extract-id; statuses carry the same id the node's metadata does (see
   `minorodata.sandbox.subs/enrich-node`). nil when the entity isn't in the graph."
  [db kind entity-id]
  (->> (get-in db [:dependency-graph :raw dag-id :nodes])
       (some (fn [{:keys [node-id type name metadata]}]
               (when (= entity-id (get metadata kind))
                 {:anchor-id node-id :anchor-type type :label name})))))

;; Clicking a failure in the failures sidebar opens the subpipeline viewer anchored
;; at that node and centers it. The bottom-pane actions (execution timeline, logs,
;; Kai, pipeline editor) live behind the row's kebab menu instead — see
;; `minorodata.components.dependency-graph.sidebar`.
(reg-event-fx
 :dependency-graph.events/reveal-node
 (fn [{:keys [db]} [_ {:keys [transform-id extract-id]}]]
   (let [[kind entity-id] (if transform-id
                            [:transform-id transform-id]
                            [:extract-id extract-id])
         anchor           (node-by-entity db kind entity-id)]
     (if anchor
       {:dispatch-n [[:dependency-graph.events/open-anchored-tab anchor]
                     [:dependency-graph.events/focus-node (:anchor-id anchor)]]}
       {:dispatch [:app.events/add-snack :warning
                   "This node isn't in the current dependency graph"]}))))

;; Arriving from elsewhere in the app with an entity named (`/dag?focus=<master-id>`
;; — see `components.dag.utils/go-to-dag` and the extract drawer's pipeline preview).
;; Opens it in the preview tab (see `open-anchored-tab`), and warns when the
;; Extracts toggle or a filter hides it.
(defn- drawn-on-pipelines-tab? [node-id]
  (some #(= node-id (:id %))
        (:pipeline-dag/nodes (datascript/pull @conn [:pipeline-dag/nodes] [:pipeline-dag/id dag-id]))))

(reg-event-fx
 :dependency-graph.events/focus-linked-node
 (fn [{:keys [db]} [_ id]]
   (if-let [{:keys [type name]} (some #(when (= id (:node-id %)) %)
                                      (get-in db [:dependency-graph :raw dag-id :nodes]))]
     {:dispatch-n (cond-> [[:dependency-graph.events/open-anchored-tab
                            {:anchor-id id :anchor-type type :label name :preview? true}]
                           [:dependency-graph.events/focus-node id]]
                    (not (drawn-on-pipelines-tab? id))
                    (conj [:app.events/add-snack :warning
                           "That entity is hidden in the current view, by the Extracts toggle or a filter"]))}
     {:dispatch [:app.events/add-snack :warning
                 "That entity isn't in the dependency graph"]})))

;; "Focus on graph" — unlike `reveal-node` (which opens a subpipeline tab anchored
;; at the node), this switches to the main "Pipelines" tab and centers the node in
;; the full graph. `focus-node` drives `ui.dag/viewport-controller`, which centers the
;; viewport on the matching node in whichever tab is active.
(reg-event-fx
 :dependency-graph.events/focus-node-on-dag
 (fn [{:keys [db]} [_ {:keys [transform-id extract-id]}]]
   (let [[kind entity-id] (if transform-id
                            [:transform-id transform-id]
                            [:extract-id extract-id])
         anchor           (node-by-entity db kind entity-id)]
     (if anchor
       {:dispatch-n [[:dependency-graph.events/focus-node (:anchor-id anchor)]]}
       {:dispatch [:app.events/add-snack :warning
                   "This node isn't in the current dependency graph"]}))))

(reg-event-fx
 :dependency-graph.events/cleanup
 (fn [{:keys [db]} _]
   {:db (dissoc db :dependency-graph)
    :dependency-graph/auto-refresh-timer nil}))
